import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupsIcon from "@mui/icons-material/Groups";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTheme } from "../../../context/ThemeContext";
import { ownerSteps, tenantSteps } from "../data";

const StepCard = ({ steps, title, icon, bgColor, iconBgColor, iconColor }) => {
  return (
    <Paper sx={{ p: 4, borderRadius: 3, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Avatar sx={{ bgcolor: bgColor, width: 50, height: 50, mr: 2 }}>
          {icon}
        </Avatar>
        <Typography variant="h5" fontWeight={700}>{title}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {steps.map((step, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                bgcolor: iconBgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: iconColor,
                flexShrink: 0,
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: -8,
                  left: -8,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: bgColor,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {index + 1}
              </Typography>
              {step.icon}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{step.title}</Typography>
              <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ── Refund, Return & Cancellation Policy Steps ──
const refundSteps = [
  {
    icon: <AccountBalanceWalletIcon />,
    title: "Booking Amount Held Securely",
    desc: "When a tenant submits a booking request, the payment is immediately held in the OwnerTenantSolution secure digital wallet. No amount is transferred to the owner until a booking is officially confirmed.",
  },
  {
    icon: <GroupsIcon />,
    title: "Owner Selects One Applicant",
    desc: "If multiple tenants apply for the same property, the owner reviews all requests and selects the single most suitable applicant. Only one booking is confirmed per property at a time.",
  },
  {
    icon: <ReplayIcon />,
    title: "Full Refund for Unsuccessful Applicants",
    desc: "All tenants whose requests were not selected by the owner receive a complete, automatic refund of their booking amount within 3–7 business days — directly to their original payment method or platform wallet.",
  },
  {
    icon: <CheckCircleOutlineIcon />,
    title: "Cancellation Before Owner Confirmation — Full Refund",
    desc: "If a tenant chooses to cancel their booking request before the owner has confirmed it, they are entitled to a full refund of their booking amount. The payment will be returned to their wallet or original payment method within 3–7 business days.",
  },
  {
    icon: <CancelIcon />,
    title: "Cancellation After Owner Confirmation — No Refund",
    desc: "If a tenant cancels their booking after the owner has already confirmed it, no refund will be issued. By accepting confirmation, the tenant enters into a binding rental commitment. We strongly advise tenants to ensure their decision is final before the owner proceeds with confirmation.",
  },
];

const HowItWorks = () => {
  const { theme } = useTheme();

  return (
    <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">

        {/* ── How It Works Header ── */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Chip label="Simple Process" sx={{ bgcolor: theme.lightBg, color: theme.primary, fontWeight: 600, mb: 2 }} />
          <Typography variant="h3" fontWeight={700} sx={{ mb: 2, color: "#1a1a2e" }}>
            How It Works
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto" }}>
            Get started in four simple steps - whether you're looking to list your property or find your next home.
          </Typography>
        </Box>

        {/* ── Owner & Tenant Steps (ORIGINAL - UNCHANGED) ── */}
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <StepCard
              steps={ownerSteps}
              title="Property Owners"
              icon={<HomeWorkIcon />}
              bgColor={theme.primary}
              iconBgColor={theme.lightBg}
              iconColor={theme.primary}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StepCard
              steps={tenantSteps}
              title="Tenants"
              icon={<PeopleIcon />}
              bgColor="#667eea"
              iconBgColor="#eef1fd"
              iconColor="#667eea"
            />
          </Grid>
        </Grid>

        {/* ════════════════════════════════════════════════
            Refund, Return & Cancellation Policy — NEW
        ════════════════════════════════════════════════ */}
        <Box sx={{ textAlign: "center", mt: 12, mb: 6 }}>
          <Chip
            label="Refund & Cancellation"
            sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600, mb: 2 }}
          />
          <Typography variant="h3" fontWeight={700} sx={{ mb: 2, color: "#1a1a2e" }}>
            Refund, Return & Cancellation Policy
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: "auto" }}>
            At OwnerTenantSolution, we are committed to ensuring a fair and transparent
            experience for every user. Our refund and cancellation process is fully
            automated, secure, and designed to protect both owners and tenants at every stage.
          </Typography>
        </Box>

        <Grid container justifyContent="center">
          <Grid item xs={12} md={10}>
            <StepCard
              steps={refundSteps}
              title="How Our Refund & Cancellation Process Works"
              icon={<ReplayIcon />}
              bgColor="#e65100"
              iconBgColor="#fff3e0"
              iconColor="#e65100"
            />
          </Grid>
        </Grid>

        {/* ── Policy Note ── */}
        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            bgcolor: "#fff8f0",
            border: "1px solid #ffe0b2",
            maxWidth: 820,
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            <strong style={{ color: "#e65100" }}>Important: </strong>
            All refunds are processed automatically by the OwnerTenantSolution platform.
            Refund timelines may vary depending on your bank or payment provider.
            For any refund-related queries, please contact our support team at{" "}
            <strong>ownertenantsolution@gmail.com</strong> or call{" "}
            <strong>+92 349 2565577</strong>. We aim to resolve all queries within 24–48 business hours.
          </Typography>
        </Box>

      </Container>
    </Box>
  );
};

export default HowItWorks;